import {ofType} from 'redux-observable';
import {Observable} from 'rxjs';
import {filter, map, mergeMap} from 'rxjs/operators';
import moment from 'moment-timezone';

import {combineEpics} from 'web-app/util/redux-observable';
import * as LeaveRegistrationActions from 'web-app/react/profile/child-profile/leave-registration/actions';
import * as LeaveWidgetActions from 'web-app/react/profile/leave-widget/actions';
import * as HolidayRSVPActions from 'web-app/react/profile/child-profile/holiday-rsvp/actions';
import MealRegistrationActionTypes from 'web-app/react/entities/meals/food-consumption/action-types';
import * as ApplyCreamModalActions from 'web-app/react/modals-container/apply-cream/actions';
import * as DiaperModalActions from 'web-app/react/modals-container/diaper/actions';
import * as SleepModalActions from 'web-app/react/modals-container/sleep/actions';
import * as ChildEpics from 'web-app/react/entities/children/epics';
import * as ChildPickupActions from 'web-app/react/modals-container/child-pickup-modal/actions';
import {deleteChildCalendarLeaveObservable} from 'web-app/react/profile/leave-widget/epics';
import {deleteMealRegistrationObservable} from 'web-app/react/entities/meals/food-consumption/epics';
import {deleteNoteObservable} from 'web-app/react/profile/child-profile/notes/api';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import * as Selectors from './selectors';
import * as Actions from './actions';
import * as Api from './api';
import * as Constants from './constants';
import {DAYS_TO_CRAWL, MAX_STEP} from './constants';

const fetchRecursive = (day, childId, isFuture = false, step = 1, hadNothingButClosingDaysInPrevious = false) => {
    const direction = isFuture ? 1 : -1;

    const rangeStart = moment(day).format('YYYY-MM-DD');
    const rangeEnd = moment(day)
        .add(direction * (step * DAYS_TO_CRAWL), 'day')
        .format('YYYY-MM-DD');

    const from = isFuture ? rangeStart : rangeEnd;
    const to = isFuture ? rangeEnd : rangeStart;

    const nextDay = moment(rangeEnd).add(direction, 'day').format('YYYY-MM-DD');

    return Api.fetchEvents('RANGE', from, to, null, childId, undefined).mergeMap(daysFromApi => {
        /*
                We turn off infinite scroll in the case where we twice in a row
                Get nothing but closing days or no events at all
             */

        const hasDaysWithEvents = daysFromApi.some(day => Boolean(day.events.size));
        const hasOtherTypesThenClosingDays =
            hasDaysWithEvents &&
            daysFromApi
                .flatMap(day => day.events)
                .filter(event =>
                    event.originator ? event.originator.type !== Constants.ORIGINATOR_TYPES.CLOSING_DAY : true,
                ).size > 0;

        const shouldStopRecursion =
            hasDaysWithEvents ||
            step >= MAX_STEP ||
            (hadNothingButClosingDaysInPrevious && !hasOtherTypesThenClosingDays);

        if (shouldStopRecursion) {
            return Observable.of({
                response: daysFromApi,
                day: nextDay,
                ceilingReached: step >= MAX_STEP,
                shouldStopInfiniteScroll: !hasOtherTypesThenClosingDays || !hasDaysWithEvents,
            });
        }

        return fetchRecursive(nextDay, childId, isFuture, ++step, !hasOtherTypesThenClosingDays);
    });
};

const fetch = action$ =>
    action$.ofType(Actions.fetch.type).mergeMap(({payload}) =>
        fetchRecursive(moment().format('YYYY-MM-DD'), payload.childId)
            .map(({response, day, ceilingReached, shouldStopInfiniteScroll}) =>
                Actions.fetchSuccess.action(response, payload.childId, day, ceilingReached, shouldStopInfiniteScroll),
            )
            .catch(e => Observable.of(Actions.fetchFailed.action(e))),
    );

const fetchFuture = action$ =>
    action$.ofType(Actions.fetchFuture.type).mergeMap(({payload}) => {
        return fetchRecursive(payload.day, payload.childId, true)
            .map(({response, day, ceilingReached}) =>
                Actions.fetchFutureSuccess.action(day, response, ceilingReached, payload.childId),
            )
            .catch(e => Observable.of(Actions.fetchFutureFailed.action(e)));
    });

const fetchPast = action$ =>
    action$.ofType(Actions.fetchPast.type).mergeMap(({payload}) =>
        fetchRecursive(payload.day, payload.childId)
            .map(({response, day, ceilingReached, shouldStopInfiniteScroll}) =>
                Actions.fetchPastSuccess.action(
                    day,
                    response,
                    ceilingReached,
                    shouldStopInfiniteScroll,
                    payload.childId,
                ),
            )
            .catch(e => Observable.of(Actions.fetchPastFailed.action(e))),
    );

const getEventObservable = (event, options) => {
    switch (event.type) {
        case Constants.LEAVE_TYPES.VACATION:
        case Constants.LEAVE_TYPES.SICK:
        case Constants.LEAVE_TYPES.ABSENT:
            return deleteChildCalendarLeaveObservable(event.childId, event.originator.id, event.date, event.date);
        case Constants.ORIGINATOR_TYPES.MEAL_REGISTRATION:
            return deleteMealRegistrationObservable(event.embed.get('id'));
        case Constants.ORIGINATOR_TYPES.NOTE:
            return deleteNoteObservable(event.originator.id);
        case Constants.ORIGINATOR_TYPES.NAP:
            return Api.deleteSleep(event.originator.id);
        case Constants.ACTION_TYPES.TOILETVISIT:
        case Constants.ACTION_TYPES.SLEEP_CHECK:
        case Constants.ACTION_TYPES.DIAPERCHANGE:
        case Constants.ACTION_TYPES.CREAM_APPLICATION:
        case Constants.ACTION_TYPES.TEMPERATURE_TAKEN:
        case Constants.ACTION_TYPES.WASHED_HANDS:
        case Constants.ACTION_TYPES.DIAPER_TOILET_ACCIDENT:
            return Api.deleteAction(event.originator.id).map(() => ({}));
        case Constants.ORIGINATOR_TYPES.PLANNED_CHECKOUT:
            if (options && options.allOccurrences) {
                return Api.deletePlannedCheckout(event.embed.get('child').get('childId'), event.originator.id);
            }
            return Api.deletePlannedCheckoutDate(
                event.embed.get('child').get('childId'),
                event.originator.id,
                event.from.format('YYYY-MM-DD'),
            );
        case Constants.ORIGINATOR_TYPES.CHECK_IN:
            return Api.deleteCheckin(event.originator.id);
        case Constants.ORIGINATOR_TYPES.STATUS_REGISTRATION:
            return Api.deleteStatus(event.originator.id);
        default:
            return Observable.of({});
    }
};

const deleteEvent = action$ =>
    action$.ofType(Actions.deleteEvent.type).mergeMap(({payload}) => {
        const {event, options} = payload;
        return getEventObservable(event, options)
            .flatMap(successAction => {
                if (options && options.allOccurrences) {
                    return [successAction, Actions.reset.action()].filter(a => a && a.type);
                }
                return [successAction, Actions.deleteEventSuccess.action(event)].filter(a => a && a.type);
            })
            .catch(() => Observable.of(Actions.deleteEventFailed.action(event)));
    });

const refetchDays = (action$, state$) =>
    action$.ofType(Actions.refetchDays.type).mergeMap(({payload}) => {
        const {pastDate, futureDate} = state$.value.childProfile.activity;
        const pastCompareDate = pastDate.clone();
        const futureCompareDate = futureDate.clone().subtract(1, 'day');

        const isStartDateWithinRange = moment(payload.startDate).isBetween(
            pastCompareDate,
            futureCompareDate,
            'day',
            '[]',
        );
        const isEndDateWithinRange = moment(payload.endDate).isBetween(pastCompareDate, futureCompareDate, 'day', '[]');

        // Check if requested date range overlaps visible range
        if (payload.startDate && payload.endDate && !isStartDateWithinRange && !isEndDateWithinRange) {
            return Observable.empty();
        }

        // Trim requested date range to match visible range's
        const startDate =
            payload.startDate && moment(payload.startDate).isSameOrAfter(pastCompareDate, 'days')
                ? payload.startDate
                : pastCompareDate.format('YYYY-MM-DD');
        const endDate =
            payload.endDate && moment(payload.endDate).isSameOrBefore(futureCompareDate, 'days')
                ? payload.endDate
                : futureCompareDate.format('YYYY-MM-DD');

        return Api.fetchEvents('RANGE', startDate, endDate, null, payload.childId, undefined)
            .map(response => Actions.refetchDaysSuccess.action(response, payload.childId))
            .catch(() => Observable.of(Actions.refetchDaysFailed.action(startDate, endDate)));
    });

const findAndUpdateDay = (action$, state$) => {
    const mealRegistrationUpdateObs = action$
        .ofType(MealRegistrationActionTypes.MEAL_REGISTRATION_UPDATE_SUCCESS)
        .delay(1000)
        .map(({payload}) => {
            const state = state$.value;
            const days = Selectors.daysFromState(state);
            const dayToUpdate = days.find(day =>
                day.events.some(event => event.embed.get('id') === payload.mealRegistrationId),
            );

            const oldMealRegistrationDate = moment(dayToUpdate.unFormattedDate).format('YYYY-MM-DD');
            const updatedMealRegistrationDate = moment(payload.updatedMealRegistration.createdAt).format('YYYY-MM-DD');

            if (oldMealRegistrationDate && updatedMealRegistrationDate) {
                if (oldMealRegistrationDate === updatedMealRegistrationDate) {
                    return {
                        dates: [oldMealRegistrationDate],
                    };
                } else {
                    return {
                        dates: [oldMealRegistrationDate, updatedMealRegistrationDate],
                    };
                }
            }

            return null;
        });

    const newChildLoaded = action$
        .ofType(Actions.fetchSuccess.type)
        .distinctUntilChanged(({payload: payload1}, {payload: payload2}) => payload1.childId === payload2.childId);

    return (
        Observable.combineLatest(
            // Every time a new child is loaded, we clear the stream of meal update successful with the null value to avoid unintended playbacks
            Observable.merge(
                mealRegistrationUpdateObs,
                newChildLoaded.map(() => null),
            ),
            newChildLoaded,
        ) as any
    ).mergeMap(([changeParams, {payload}]) => {
        if (changeParams) {
            if (changeParams.dates) {
                return changeParams.dates.map(date => {
                    return Actions.refetchDays.action(date, date, payload.childId);
                });
            } else {
                return Observable.of(
                    Actions.refetchDays.action(changeParams.startDate, changeParams.endDate, payload.childId),
                );
            }
        }
        return Observable.empty();
    });
};

const reset = action$ => {
    const resetObs = action$.ofType(Actions.reset.type, HolidayRSVPActions.updateSuccess.type);

    return (
        resetObs
            .withLatestFrom(
                action$
                    .ofType(Actions.fetchSuccess.type)
                    .map(({payload}) => payload.childId)
                    .distinctUntilChanged(),
            )
            .delay(1000)
            // eslint-disable-next-line no-unused-vars
            .map(([, childId]) => Actions.fetch.action(childId))
    );
};

const listenAndUpdate: any = action$ => {
    const mealRegistrationCreationObs = action$
        .ofType(MealRegistrationActionTypes.MEAL_REGISTRATIONS_CREATE_SUCCESS)
        .map(({payload}) => {
            if (!payload.persistedMealRegistrations) {
                return null;
            }

            const mealRegistration = payload.persistedMealRegistrations[0];

            if (!mealRegistration) {
                return null;
            }

            const date = moment(mealRegistration.createdAt).format('YYYY-MM-DD');

            return {
                startDate: date,
                endDate: date,
            };
        });

    const updateTodayObs = action$
        .ofType(
            DiaperModalActions.diaperSuccess.type,
            ApplyCreamModalActions.applyCreamSuccess.type,
            SleepModalActions.checkOnSuccess.type,
            SleepModalActions.napSuccess.type,
            Actions.checkInSuccess.type,
            Actions.checkOutSuccess.type,
            ChildPickupActions.updateCheckinSuccess.type,
        )
        .map(() => ({
            startDate: moment().format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),
        }));

    const leaveUpdateObs = action$.ofType(LeaveRegistrationActions.updateSuccess.type).map(({payload}) => ({
        startDate: payload.startDate,
        endDate: payload.endDate,
    }));

    const deleteLeaveObs = action$.ofType(LeaveWidgetActions.deleteChildLeaveSuccess.type).map(({payload}) => ({
        startDate: payload.startDate,
        endDate: payload.endDate || payload.startDate,
    }));

    const childIdObservable = action$
        .ofType(Actions.fetchSuccess.type)
        .map(({payload}) => payload.childId)
        .distinctUntilChanged();

    const changesObservable = Observable.merge(
        leaveUpdateObs,
        deleteLeaveObs,
        updateTodayObs,
        mealRegistrationCreationObs,
    );

    const clearObservable = action$.ofType(Actions.fetch.type).mapTo(null);

    const trigger = Observable.combineLatest(childIdObservable, Observable.merge(changesObservable, clearObservable));

    /*
        To avoid consequences of replica lag we fetch updates twice. Initially we just had a delay of 1s, but
        we still got reports of updates not being reflected in the UI.

        New strategy:
            - Fetch twice where one is delayed more than the other

        Shortcomings:
            - Updates are still fetched if you navigate away from the view
     */
    return Observable.merge(trigger.delay(1000), trigger.delay(4000))
        .map(([childId, changeParams]) => {
            if (!changeParams) {
                return null;
            }
            return Actions.refetchDays.action((changeParams as any).startDate, (changeParams as any).endDate, childId);
        })
        .filter(a => hasValue(a));
};

const checkIn = action$ =>
    action$.ofType(Actions.checkIn.type).mergeMap(({payload}) =>
        ChildEpics.checkInObservable(payload.childId, payload.groupId, payload.showWarningOnCheckIn)
            .flatMap(successActions => [...successActions, Actions.checkInSuccess.action()])
            .catch(e => Observable.of(Actions.checkInFailed.action(e))),
    );

const checkOut = action$ =>
    action$.ofType(Actions.checkOut.type).mergeMap(({payload}) =>
        ChildEpics.checkOutObservable(payload.childId)
            .flatMap(successActions => [...successActions, Actions.checkOutSuccess.action()])
            .catch(e => Observable.of(Actions.checkOutFailed.action(e))),
    );

// day of the event must be refetched because the backend deletes
// also events that have the same originator(checkout, expected checkout)
const refetchDayOnChildCheckInDeletion = action$ =>
    action$.pipe(
        ofType(Actions.deleteEventSuccess.type),
        filter(({payload}) => payload.event.type === Constants.ORIGINATOR_TYPES.CHECK_IN),
        map((({payload}) => {
            const {event} = payload;
            return Actions.refetchDays.action(event.date, event.date, event.childId);
        }) as any),
    );

const refetchChildOnChildCheckInDeletion = action$ =>
    action$.pipe(
        ofType(Actions.deleteEventSuccess.type),
        filter(({payload}) => payload.event.type === Constants.ORIGINATOR_TYPES.CHECK_IN),
        mergeMap(({payload}) => {
            return ChildEpics.fetchChildObservable(payload.event.childId);
        }),
    );

export default combineEpics(
    reset,
    fetch,
    fetchFuture,
    fetchPast,
    deleteEvent,
    listenAndUpdate,
    findAndUpdateDay,
    refetchDays,
    checkIn,
    checkOut,
    refetchDayOnChildCheckInDeletion,
    refetchChildOnChildCheckInDeletion,
);
